import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Empty,
  Spin,
  Popconfirm,
  Input,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  GiftOutlined,
  QrcodeOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { API_URL, getCredentials } from "../../shared/utils/Util";
import { getFetchT } from "../../shared/utils/TypedUtil";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import { PointGiftCard, ListPointGiftCardsResponse } from "./types";
import { PointGiftCardForm } from "./Form";

const { Text } = Typography;

type PointGiftCardEditorProps = {
  restaurantId: string;
};

export const PointGiftCardEditor: React.FC<PointGiftCardEditorProps> = ({ restaurantId }) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { deleteSuccess, notifyError, showError } = useNotification();
  const [giftCards, setGiftCards] = useState<PointGiftCard[] | undefined>(undefined);
  const [allGiftCards, setAllGiftCards] = useState<PointGiftCard[]>([]);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const PAGE_SIZE = 10;

  const fetchGiftCards = useCallback(
    (cursor?: number, append = false) => {
      if (!append) {
        setGiftCards(undefined);
      } else {
        setLoadingMore(true);
      }

      let url = `${API_URL}/v1/r/${restaurantId}/admin/point_gift_cards?limit=100`;
      if (cursor !== undefined) {
        url += `&cursor=${cursor}`;
      }

      getFetchT<ListPointGiftCardsResponse>(
        url,
        (result) => {
          const newCards = result.gift_cards || [];
          if (append) {
            setAllGiftCards((prev) => [...prev, ...newCards]);
          } else {
            setAllGiftCards(newCards);
          }
          setGiftCards(append ? [...allGiftCards, ...newCards] : newCards);
          setHasMore(result.next_cursor !== undefined);
          setLoadingMore(false);

          if (result.next_cursor !== undefined) {
            fetchGiftCards(result.next_cursor, true);
          }
        },
        (err) => {
          notifyError(t("failed_to_fetch_gift_cards"), err);
          setGiftCards([]);
          setAllGiftCards([]);
          setLoadingMore(false);
        }
      );
    },
    [restaurantId, t, notifyError, allGiftCards]
  );

  const refreshGiftCards = useCallback(() => {
    setAllGiftCards([]);
    setCurrentPage(1);
    setSearchText("");
    fetchGiftCards();
  }, [fetchGiftCards]);

  useEffect(() => {
    fetchGiftCards();
  }, [restaurantId]);

  const handleDelete = (giftCardId: string) => {
    setDeleting(giftCardId);
    fetch(`${API_URL}/v1/r/${restaurantId}/admin/point_gift_cards/${giftCardId}`, {
      credentials: "include",
      method: "DELETE",
      headers: {
        Authorization: `Basic ${getCredentials()}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw res;
        }
        deleteSuccess(t("point_gift_card"));
        refreshGiftCards();
      })
      .catch((err) => {
        err.text().then((errorMessage: string) => {
          notifyError(t("failed_to_delete_gift_card"), errorMessage);
        });
      })
      .finally(() => {
        setDeleting(null);
      });
  };

  const handleDownloadQRCode = async (giftCard: PointGiftCard) => {
    setDownloading(giftCard.id);
    try {
      const response = await fetch(
        `${API_URL}/v1/r/${restaurantId}/admin/point_gift_cards/${giftCard.id}/label`,
        {
          credentials: "include",
          headers: {
            Authorization: `Basic ${getCredentials()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download QR code");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${giftCard.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showError(t("failed_to_download_qr_code"));
    } finally {
      setDownloading(null);
    }
  };

  const onChange = () => {
    refreshGiftCards();
  };

  const filteredGiftCards = allGiftCards.filter((card) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    const amountMatch = card.amount.toString().includes(searchText);
    const descriptionMatch = card.description.toLowerCase().includes(searchLower);
    return amountMatch || descriptionMatch;
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (giftCards === undefined) {
    return <Spin size="large" />;
  }

  const columns = [
    {
      title: t("description"),
      dataIndex: "description",
      key: "description",
    },
    {
      title: t("amount"),
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: t("expiry_date"),
      dataIndex: "expire_time",
      key: "expire_time",
      render: (value: string | undefined) => formatDate(value),
    },
    {
      title: t("claimed"),
      dataIndex: "is_claimed",
      key: "is_claimed",
      render: (value: boolean) => (value ? t("yes") : t("no")),
    },
    {
      title: t("create_time"),
      dataIndex: "create_time",
      key: "create_time",
      render: (value: string) => formatDateTime(value),
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_: unknown, record: PointGiftCard) => (
        <Space>
          <Tooltip title={record.is_claimed ? t("cannot_delete_claimed_card") : ""}>
            <span>
              <Popconfirm
                title={t("delete_gift_card_confirm")}
                description={t("delete_gift_card_description")}
                onConfirm={() => handleDelete(record.id)}
                okText={t("yes")}
                cancelText={t("no")}
                disabled={record.is_claimed}
              >
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleting === record.id}
                  disabled={record.is_claimed}
                >
                  {t("delete")}
                </Button>
              </Popconfirm>
            </span>
          </Tooltip>
          <Button
            type="text"
            icon={<QrcodeOutlined />}
            loading={downloading === record.id}
            onClick={() => handleDownloadQRCode(record)}
          >
            {t("qr_code")}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <PlusOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("add_point_gift_card")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
          marginBottom: token.marginXL,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <PointGiftCardForm restaurantId={restaurantId} onSuccess={onChange} />
      </Card>

      <Card
        title={
          <Space>
            <GiftOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("current_gift_cards")}
            </Text>
          </Space>
        }
        extra={
          <Input
            placeholder={t("search_by_amount_or_description")}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentPage(1);
            }}
            style={{ width: 250 }}
            allowClear
          />
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        {filteredGiftCards.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary">
                {searchText ? t("no_gift_cards_match_search") : t("no_gift_cards_available")}
              </Text>
            }
          />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredGiftCards}
            rowKey="id"
            loading={loadingMore}
            pagination={{
              current: currentPage,
              pageSize: PAGE_SIZE,
              total: filteredGiftCards.length,
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: false,
              showTotal: (total) => `${t("total")} ${total} ${t("gift_cards")}`,
            }}
            style={{ borderRadius: token.borderRadiusLG }}
          />
        )}
      </Card>
    </div>
  );
};
